import { useEffect, useState } from 'react';

export type Theme = 'light' | 'dark';

const KEY = 'ea-theme';
const EVENT = 'ea:theme';

const isTheme = (v: unknown): v is Theme => v === 'light' || v === 'dark';

function fromCookie(): Theme | null {
  const m = /(?:^|;\s*)ea-theme=(light|dark)(?:;|$)/.exec(document.cookie);
  return m && isTheme(m[1]) ? m[1] : null;
}

function fromStorage(): Theme | null {
  try {
    const v = localStorage.getItem(KEY);
    return isTheme(v) ? v : null;
  } catch {
    return null;
  }
}

/**
 * The theme the learner chose. The cookie is what the Worker reads to paint the shell before the
 * app runs; localStorage is the fallback for a browser that dropped the cookie. Light by default.
 */
export function getTheme(): Theme {
  return fromCookie() ?? fromStorage() ?? 'light';
}

/** Writes both stores, paints the document, and tells listeners (the tenant accent re-applies). */
export function setTheme(theme: Theme): void {
  document.cookie = `${KEY}=${theme}; Path=/; Max-Age=31536000; SameSite=Lax`;
  try {
    localStorage.setItem(KEY, theme);
  } catch {
    // Storage may be unavailable in a private window; the cookie carries the choice.
  }
  document.documentElement.setAttribute('data-theme', theme);
  window.dispatchEvent(new CustomEvent<Theme>(EVENT, { detail: theme }));
}

export function onThemeChange(handler: (theme: Theme) => void): () => void {
  const listener = (e: Event) => {
    const detail = (e as CustomEvent<unknown>).detail;
    if (isTheme(detail)) handler(detail);
  };
  window.addEventListener(EVENT, listener);
  return () => window.removeEventListener(EVENT, listener);
}

export function useTheme(): [Theme, (t: Theme) => void] {
  const [theme, set] = useState<Theme>(() => getTheme());
  useEffect(() => onThemeChange(set), []);
  return [theme, setTheme];
}
